import { useEffect, useRef, useState } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';

// 24 popular languages — Bangla included as required
export const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'bn', name: 'Bangla', native: '\u09AC\u09BE\u0982\u09B2\u09BE', flag: '\u{1F1E7}\u{1F1E9}' },
  { code: 'hi', name: 'Hindi', native: '\u0939\u093F\u0928\u094D\u0926\u0940', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: 'ur', name: 'Urdu', native: '\u0627\u0631\u062F\u0648', flag: '\u{1F1F5}\u{1F1F0}' },
  { code: 'ar', name: 'Arabic', native: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629', flag: '\u{1F1F8}\u{1F1E6}' },
  { code: 'es', name: 'Spanish', native: 'Espa\u00F1ol', flag: '\u{1F1EA}\u{1F1F8}' },
  { code: 'pt', name: 'Portuguese', native: 'Portugu\u00EAs', flag: '\u{1F1F5}\u{1F1F9}' },
  { code: 'fr', name: 'French', native: 'Fran\u00E7ais', flag: '\u{1F1EB}\u{1F1F7}' },
  { code: 'de', name: 'German', native: 'Deutsch', flag: '\u{1F1E9}\u{1F1EA}' },
  { code: 'it', name: 'Italian', native: 'Italiano', flag: '\u{1F1EE}\u{1F1F9}' },
  { code: 'ru', name: 'Russian', native: '\u0420\u0443\u0441\u0441\u043A\u0438\u0439', flag: '\u{1F1F7}\u{1F1FA}' },
  { code: 'tr', name: 'Turkish', native: 'T\u00FCrk\u00E7e', flag: '\u{1F1F9}\u{1F1F7}' },
  { code: 'fa', name: 'Persian', native: '\u0641\u0627\u0631\u0633\u06CC', flag: '\u{1F1EE}\u{1F1F7}' },
  { code: 'zh-CN', name: 'Chinese', native: '\u4E2D\u6587', flag: '\u{1F1E8}\u{1F1F3}' },
  { code: 'ja', name: 'Japanese', native: '\u65E5\u672C\u8A9E', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: 'ko', name: 'Korean', native: '\uD55C\uAD6D\uC5B4', flag: '\u{1F1F0}\u{1F1F7}' },
  { code: 'id', name: 'Indonesian', native: 'Indonesia', flag: '\u{1F1EE}\u{1F1E9}' },
  { code: 'ms', name: 'Malay', native: 'Bahasa Melayu', flag: '\u{1F1F2}\u{1F1FE}' },
  { code: 'th', name: 'Thai', native: '\u0E44\u0E17\u0E22', flag: '\u{1F1F9}\u{1F1ED}' },
  { code: 'vi', name: 'Vietnamese', native: 'Ti\u1EBFng Vi\u1EC7t', flag: '\u{1F1FB}\u{1F1F3}' },
  { code: 'tl', name: 'Filipino', native: 'Filipino', flag: '\u{1F1F5}\u{1F1ED}' },
  { code: 'sw', name: 'Swahili', native: 'Kiswahili', flag: '\u{1F1F0}\u{1F1EA}' },
  { code: 'pl', name: 'Polish', native: 'Polski', flag: '\u{1F1F5}\u{1F1F1}' },
  { code: 'nl', name: 'Dutch', native: 'Nederlands', flag: '\u{1F1F3}\u{1F1F1}' },
];

const COOKIE_NAME = 'googtrans';

function readCurrentLang() {
  if (typeof document === 'undefined') return 'en';
  const m = document.cookie.match(/googtrans=\/[^/]+\/([^;]+)/);
  if (!m) return 'en';
  const v = m[1];
  return v === 'en' ? 'en' : v;
}

function buildDomains() {
  const host = window.location.hostname;
  const parts = host.split('.');
  const domains = new Set(['', host, '.' + host]);
  // walk up parent domains: .a.b.c, .b.c
  for (let i = 1; i < parts.length - 1; i++) {
    domains.add('.' + parts.slice(i).join('.'));
  }
  // also bare last 2/3 segments
  if (parts.length >= 2) domains.add('.' + parts.slice(-2).join('.'));
  if (parts.length >= 3) domains.add('.' + parts.slice(-3).join('.'));
  return Array.from(domains);
}

function clearAllGoogtransCookies() {
  const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
  const domains = buildDomains();
  const paths = ['/', ''];
  for (const d of domains) {
    for (const p of paths) {
      const domAttr = d ? `; domain=${d}` : '';
      const pathAttr = p ? `; path=${p}` : '';
      document.cookie = `googtrans=; expires=${expires}${pathAttr}${domAttr}`;
    }
  }
}

function setLanguage(code) {
  // Always clear first so we never end up with stacked cookies
  clearAllGoogtransCookies();
  if (code && code !== 'en') {
    const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
    const value = `/auto/${code}`;
    const domains = buildDomains();
    for (const d of domains) {
      const domAttr = d ? `; domain=${d}` : '';
      document.cookie = `googtrans=${value}; expires=${expires}; path=/${domAttr}`;
    }
  }
  // Hard reload (bypass cache) so Google Translate re-initializes from scratch
  try { window.location.reload(true); } catch { window.location.reload(); }
}

export default function LanguageSwitcher({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('en');
  const [filter, setFilter] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    setCurrent(readCurrentLang());
  }, []);

  useEffect(() => {
    function onDocClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const active = LANGUAGES.find(l => l.code === current) || LANGUAGES[0];
  const filtered = filter
    ? LANGUAGES.filter(l => (l.name + ' ' + l.native + ' ' + l.code).toLowerCase().includes(filter.toLowerCase()))
    : LANGUAGES;

  return (
    <div ref={ref} className="relative notranslate" translate="no">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="language-switcher-trigger"
        className={`inline-flex items-center gap-2 rounded-lg border border-border bg-[hsl(240_6%_10%)] hover:border-[rgba(245,185,66,0.45)] hover:bg-[hsl(240_6%_12%)] transition-colors ${compact ? 'h-9 px-2.5' : 'h-9 px-3'}`}
        aria-label="Change language"
      >
        <Globe className="h-4 w-4 text-[hsl(43_92%_56%)]" />
        <span className="text-sm">{active.flag}</span>
        {!compact && <span className="text-xs uppercase tracking-wider text-muted-foreground">{active.code.toUpperCase()}</span>}
        <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[280px] rounded-xl card-surface shadow-[0_20px_60px_rgba(0,0,0,0.6)] z-50 overflow-hidden" data-testid="language-switcher-menu">
          <div className="p-2 border-b border-border">
            <input
              autoFocus
              value={filter}
              onChange={e => setFilter(e.target.value)}
              placeholder="Search language..."
              className="w-full bg-transparent border-0 outline-none text-sm px-2 py-1.5 text-white placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-[320px] overflow-y-auto py-1">
            {filtered.map(l => (
              <button
                key={l.code}
                type="button"
                onClick={() => setLanguage(l.code)}
                data-testid={`language-option-${l.code}`}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[hsl(240_6%_12%)] transition-colors ${current === l.code ? 'bg-[rgba(245,185,66,0.08)]' : ''}`}
              >
                <span className="text-base">{l.flag}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-white truncate">{l.native}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{l.name} · {l.code.toUpperCase()}</div>
                </div>
                {current === l.code && <Check className="h-4 w-4 text-[hsl(43_92%_56%)]" />}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">No language found.</div>
            )}
          </div>
          <div className="border-t border-border px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground text-center">
            Powered by Google Translate
          </div>
        </div>
      )}
    </div>
  );
}
